import React, { useState } from 'react';
import { Smartphone, Store, Wrench, ArrowRight, User, MapPin, CheckCircle, ShieldCheck, Sparkles, Loader2, Globe } from 'lucide-react';
import { UserRole, Language, Profile } from '../types';
import { SERVICE_CATEGORIES } from '../constants';
import { db } from '../services/dbService';
// Corrected import path for useTranslation
import { useTranslation } from '../LanguageContext';

interface OnboardingProps {
  userId: string;
  email: string;
  initialName: string;
  initialAvatar?: string;
  onComplete: (profile: Profile) => void;
  onLogout: () => void;
}

type OnboardingStep = 'ROLE' | 'INFO' | 'SHOP';

export const Onboarding: React.FC<OnboardingProps> = ({ 
  userId, email, initialName, initialAvatar, onComplete, onLogout 
}) => {
  const { language, setLanguage } = useTranslation();
  const [step, setStep] = useState<OnboardingStep>('ROLE');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Profile Data
  const [role, setRole] = useState<UserRole | null>(null);
  const [fullName, setFullName] = useState(initialName);

  // Shop Data (if applicable)
  const [shopName, setShopName] = useState('');
  const [shopAddress, setShopAddress] = useState('');
  const [shopCategory, setShopCategory] = useState<string[]>([]);

  const texts: Record<string, Record<Language, string>> = {
    welcome: { EN: 'Create Your Profile', KH: 'បង្កើតព័ត៌មានផ្ទាល់ខ្លួន', ZH: '创建您的资料', JA: 'プロフィール作成', KO: '프로필 생성' },
    select_role: { EN: 'Who are you?', KH: 'តើអ្នកជាអ្នកណា?', ZH: '您是谁？', JA: 'あなたの役割は？', KO: '당신은 누구입니까?' },
    role_sub: { EN: 'Choose your primary identity on Cheang.', KH: 'ជ្រើសរើសអត្តសញ្ញាណចម្បងរបស់អ្នកនៅលើ Cheang។', ZH: '在 Cheang 上选择您的主要身份。', JA: 'Cheang での主な役割を選択してください。', KO: 'Cheang에서의 주요 정체성을 선택하십시오.' },
    customer: { EN: 'Customer', KH: 'អតិថិជន', ZH: '客户', JA: '顧客', KO: '고객' },
    customer_sub: { EN: 'I need repairs', KH: 'ខ្ញុំចង់ជួសជុលឧបករណ៍', ZH: '我需要维修', JA: '修理を依頼したい', KO: '수리가 필요합니다' },
    shop_owner: { EN: 'Shop Owner', KH: 'ម្ចាស់ហាង', ZH: '店主', JA: '店主', KO: '상점 주인' },
    shop_owner_sub: { EN: 'I own a business', KH: 'ខ្ញុំមានអាជីវកម្មជួសជុល', ZH: '我有维修业务', JA: 'ビジネスを経営している', KO: '사업을 운영 중입니다' },
    technician: { EN: 'Technician', KH: 'ជាងបច្ចេកទេស', ZH: '技师', JA: '技術者', KO: '기술자' },
    technician_sub: { EN: 'I work for a shop', KH: 'ខ្ញុំធ្វើការឱ្យហាង', ZH: '我在店铺工作', JA: 'ショップで働いている', KO: '상점에서 근무합니다' },
    personal_info: { EN: 'Personal Info', KH: 'ព័ត៌មានផ្ទាល់ខ្លួន', ZH: '个人信息', JA: '個人情報', KO: '개인 정보' },
    info_sub: { EN: 'Set up your display name.', KH: 'កំណត់ឈ្មោះសម្គាល់របស់អ្នក។', ZH: '设置您的显示名称。', JA: '表示名を設定してください。', KO: '표시 이름을 설정하십시오.' },
    full_name: { EN: 'Full Name', KH: 'ឈ្មោះពេញ', ZH: '全名', JA: '氏名', KO: '전체 이름' },
    shop_setup: { EN: 'Shop Setup', KH: 'រៀបចំបញ្ជីហាង', ZH: '店铺设置', JA: 'ショップ設定', KO: '상점 설정' },
    shop_sub: { EN: 'Basic info to get listed.', KH: 'ព័ត៌មានមូលដ្ឋានដើម្បីដាក់បញ្ជី។', ZH: '列入清单所需的基本信息。', JA: '掲載に必要な基本情報。', KO: '목록에 등록하기 위한 기본 정보입니다.' },
    shop_name: { EN: 'Shop Name', KH: 'ឈ្មោះហាង', ZH: '店铺名称', JA: 'ショップ名', KO: '상점 이름' },
    address: { EN: 'Location/Address', KH: 'ទីតាំង/អាសយដ្ឋាន', ZH: '位置/地址', JA: '住所', KO: '위치/주소' },
    continue: { EN: 'Continue', KH: 'បន្ត', ZH: '继续', JA: '続行', KO: '계속하기' },
    finish: { EN: 'Complete Setup', KH: 'បញ្ចប់ការរៀបចំ', ZH: '完成设置', JA: '設定完了', KO: '설정 완료' },
    back: { EN: 'Back', KH: 'ត្រឡប់ក្រោយ', ZH: '返回', JA: '戻る', KO: '뒤로' },
    logout: { EN: 'Cancel & Logout', KH: 'បោះបង់ និងចាកចេញ', ZH: '取消并退出', JA: 'キャンセルしてログアウト', KO: '취소 및 로그아웃' }
  };

  const handleRoleSelect = (r: UserRole) => {
    setRole(r);
    setStep('INFO');
  };

  const handleCompleteInfo = () => {
    if (!fullName) return;
    if (role === UserRole.SHOP_OWNER) {
      setStep('SHOP');
    } else {
      finalizeOnboarding();
    }
  };

  const toggleCategory = (cat: string) => {
    setShopCategory(prev => 
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const finalizeOnboarding = async () => {
    if (!role) return;
    setLoading(true);
    setError(null);
    try {
      const profileData: Partial<Profile> = {
        id: userId,
        role,
        full_name: fullName,
        email,
        avatar_url: initialAvatar
      };

      const profile = await db.createProfile(profileData);

      if (role === UserRole.SHOP_OWNER) {
        await db.createShop({
          owner_id: userId,
          name: shopName,
          address: shopAddress,
          category: shopCategory,
          description: `Welcome to ${shopName}. We specialize in high-quality repairs.`,
          contact: { phone: 'N/A' },
          workingHours: '8:00 AM - 6:00 PM',
          coordinates: { lat: 11.55, lng: 104.92 },
          imageUrl: 'https://images.unsplash.com/photo-1581092921461-eab62e97a783?auto=format&fit=crop&q=80&w=600',
          logoUrl: `https://api.dicebear.com/7.x/identicon/svg?seed=${shopName}`,
          vatPercentage: 0,
          rating: 5.0
        });
      }

      onComplete(profile);
    } catch (err: any) {
      console.error("Onboarding failed", err);
      if (err.code === '42501') {
        setError("Permission Denied: Please contact admin to enable your account creation (RLS Policy).");
      } else {
        setError(err.message || "Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[60%] rounded-full bg-violet-600/20 blur-[140px]"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[60%] rounded-full bg-fuchsia-600/20 blur-[140px]"></div>

      <div className="fixed top-8 left-1/2 -translate-x-1/2 z-50">
        <div className="bg-white/10 backdrop-blur-md p-1 rounded-2xl border border-white/10 flex items-center gap-1 shadow-2xl">
          <div className="px-3 py-1 text-white/40"><Globe size={14} /></div>
          {(['KH', 'EN', 'ZH', 'JA', 'KO'] as Language[]).map((l) => (
            <button key={l} onClick={() => setLanguage(l)} className={`px-3 py-1.5 rounded-xl text-[10px] font-black transition-all ${language === l ? 'bg-white text-slate-900' : 'text-white/40 hover:text-white'}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="w-full max-w-4xl bg-white/5 backdrop-blur-3xl rounded-[3.5rem] border border-white/10 p-10 md:p-16 relative z-10 shadow-2xl animate-in zoom-in duration-500">
        
        {/* Error Message */}
        {error && (
          <div className="mb-8 p-6 bg-red-500/10 border border-red-500/20 rounded-[2rem] text-red-400 font-bold text-sm text-center animate-in shake">
            {error}
          </div>
        )}

        <div className="flex justify-center mb-16 gap-3">
          {(['ROLE', 'INFO', 'SHOP'] as OnboardingStep[]).map((s, idx) => {
            if (s === 'SHOP' && role !== UserRole.SHOP_OWNER) return null;
            const isCurrent = step === s;
            const isPast = (step === 'INFO' && s === 'ROLE') || (step === 'SHOP' && s !== 'SHOP');
            return (
              <div key={s} className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center font-black text-xs transition-all duration-500 ${isCurrent ? 'bg-violet-600 text-white scale-110 shadow-lg shadow-violet-500/50' : isPast ? 'bg-emerald-50 text-white' : 'bg-white/10 text-white/40'}`}>{isPast ? <CheckCircle size={18} /> : idx + 1}</div>
                {idx < 2 && (s !== 'INFO' || role === UserRole.SHOP_OWNER) && <div className={`w-8 h-1 rounded-full ${isPast ? 'bg-emerald-500' : 'bg-white/10'}`}></div>}
              </div>
            );
          })}
        </div>

        {step === 'ROLE' && (
          <div className="text-center animate-in fade-in slide-in-from-bottom-4">
            <h1 className="text-4xl font-black text-white mb-3 tracking-tighter">{texts.select_role[language]}</h1>
            <p className="text-slate-400 font-bold mb-12 uppercase text-[10px] tracking-[0.3em]">{texts.role_sub[language]}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { id: UserRole.CUSTOMER, title: texts.customer[language], sub: texts.customer_sub[language], icon: Smartphone, color: 'bg-violet-500' },
                { id: UserRole.SHOP_OWNER, title: texts.shop_owner[language], sub: texts.shop_owner_sub[language], icon: Store, color: 'bg-fuchsia-500' },
                { id: UserRole.STAFF, title: texts.technician[language], sub: texts.technician_sub[language], icon: Wrench, color: 'bg-cyan-500' },
              ].map(r => (
                <button key={r.id} onClick={() => handleRoleSelect(r.id)} className="group p-8 bg-white/5 border border-white/10 rounded-[2.5rem] hover:bg-white transition-all duration-500 hover:scale-105 hover:shadow-2xl text-left">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-white mb-6 shadow-xl ${r.color}`}><r.icon size={28} /></div>
                  <h3 className="text-xl font-black text-white group-hover:text-slate-900 mb-1">{r.title}</h3>
                  <p className="text-slate-400 group-hover:text-slate-500 font-bold text-xs">{r.sub}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 'INFO' && (
          <div className="max-w-md mx-auto animate-in fade-in slide-in-from-right-8">
            <div className="text-center mb-10">
               <h1 className="text-4xl font-black text-white mb-3 tracking-tighter">{texts.personal_info[language]}</h1>
               <p className="text-slate-400 font-bold uppercase text-[10px] tracking-[0.3em]">{texts.info_sub[language]}</p>
            </div>
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-white/40 uppercase tracking-widest ml-1">
                  {texts.full_name[language]} <span className="text-rose-500 ml-0.5">*</span>
                </label>
                <div className="relative group">
                  <User className="absolute left-6 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-violet-400 transition-colors" size={20} />
                  <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} className="w-full pl-14 pr-6 py-5 rounded-2xl bg-white/5 border border-white/10 text-white font-black outline-none focus:ring-4 focus:ring-violet-500/20 transition-all shadow-inner" />
                </div>
              </div>
              <div className="pt-8 flex flex-col gap-4">
                <button onClick={handleCompleteInfo} disabled={!fullName || loading} className="w-full py-6 bg-white text-slate-900 rounded-[1.8rem] font-black text-lg hover:bg-violet-600 hover:text-white transition-all shadow-2xl flex items-center justify-center gap-3 disabled:opacity-30 group">
                  {loading ? <Loader2 className="animate-spin" /> : (role === UserRole.SHOP_OWNER ? texts.continue[language] : texts.finish[language])}
                  {!loading && <ArrowRight className="group-hover:translate-x-2 transition-transform" />}
                </button>
                <button onClick={() => setStep('ROLE')} className="text-white/40 hover:text-white font-black uppercase text-[10px] tracking-widest transition-colors">{texts.back[language]}</button>
              </div>
            </div>
          </div>
        )}

        {step === 'SHOP' && (
          <div className="max-w-2xl mx-auto animate-in fade-in slide-in-from-right-8">
            <div className="text-center mb-10">
               <h1 className="text-4xl font-black text-white mb-3 tracking-tighter">{texts.shop_setup[language]}</h1>
               <p className="text-slate-400 font-bold uppercase text-[10px] tracking-[0.3em]">{texts.shop_sub[language]}</p>
            </div>
            <div className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-white/40 uppercase tracking-widest ml-1">
                    {texts.shop_name[language]} <span className="text-rose-500 ml-0.5">*</span>
                  </label>
                  <div className="relative group">
                    <Store className="absolute left-6 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-violet-400 transition-colors" size={20} />
                    <input type="text" value={shopName} onChange={e => setShopName(e.target.value)} className="w-full pl-14 pr-6 py-5 rounded-2xl bg-white/5 border border-white/10 text-white font-black outline-none focus:ring-4 focus:ring-violet-500/20 transition-all shadow-inner" />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-white/40 uppercase tracking-widest ml-1">
                    {texts.address[language]} <span className="text-rose-500 ml-0.5">*</span>
                  </label>
                  <div className="relative group">
                    <MapPin className="absolute left-6 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-violet-400 transition-colors" size={20} />
                    <input type="text" placeholder="Phnom Penh, St..." value={shopAddress} onChange={e => setShopAddress(e.target.value)} className="w-full pl-14 pr-6 py-5 rounded-2xl bg-white/5 border border-white/10 text-white font-black outline-none focus:ring-4 focus:ring-violet-500/20 transition-all shadow-inner" />
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <label className="text-[10px] font-black text-white/40 uppercase tracking-widest ml-1">
                  Categories Served <span className="text-rose-500 ml-0.5">*</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {SERVICE_CATEGORIES.map(cat => {
                    const isSelected = shopCategory.includes(cat);
                    return <button key={cat} type="button" onClick={() => toggleCategory(cat)} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all ${isSelected ? 'bg-violet-600 border-violet-600 text-white shadow-lg' : 'bg-white/5 border-white/10 text-white/40 hover:bg-white/10 hover:text-white'}`}>{cat}</button>;
                  })}
                </div>
              </div>
              <div className="pt-8 flex flex-col gap-4">
                <button onClick={finalizeOnboarding} disabled={!shopName || !shopAddress || shopCategory.length === 0 || loading} className="w-full py-6 bg-white text-slate-900 rounded-[1.8rem] font-black text-lg hover:bg-violet-600 hover:text-white transition-all shadow-2xl flex items-center justify-center gap-3 disabled:opacity-30 group relative">
                  {loading ? <Loader2 className="animate-spin" /> : <Sparkles fill="currentColor" size={20} />}
                  {loading ? 'Initializing Business...' : texts.finish[language]}
                </button>
                <button onClick={() => setStep('INFO')} className="text-white/40 hover:text-white font-black uppercase text-[10px] tracking-widest transition-colors">{texts.back[language]}</button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col items-center">
           <div className="flex items-center gap-3 text-white/40 mb-6">
              <ShieldCheck size={14} className="text-violet-500" />
              <span className="text-[9px] font-black uppercase tracking-[0.2em]">Verified Secure Identity Setup</span>
           </div>
           <button onClick={onLogout} className="text-white/20 hover:text-red-400 font-black uppercase text-[10px] tracking-widest transition-colors">{texts.logout[language]}</button>
        </div>
      </div>
    </div>
  );
};